<template>
    <div class="n-files" ref="files">
        <h2 class="n-files__heading" v-if="showSlot('heading')"><slot name="heading">Upload files</slot></h2>
        <p class="n-files__description" v-if="showSlot('description')"><slot name="description">This component let's you upload your files.</slot></p>

        <n-files-upload v-if="showUpload === true" :allowedFileTypes="allowedFileTypes" :uploadStatus="uploadStatus" :uploadErrorResponse="uploadErrorResponse" @uploadFiles="uploadFiles">
            <template v-slot:upload-label>
                <slot name="upload-label" />
            </template>
            <template v-slot:upload-title>
                <slot name="upload-title" />
            </template>
            <template v-slot:upload-saving-singluar>
                <slot name="upload-saving-singluar" />
            </template>
            <template v-slot:upload-saving-plural>
                <slot name="upload-saving-plural" />
            </template>
            <template v-slot:upload-success-singluar>
                <slot name="upload-success-singluar" />
            </template>
            <template v-slot:upload-success-plural>
                <slot name="upload-success-plural" />
            </template>
            <template v-slot:upload-failed>
                <slot name="upload-failed" />
            </template>
        </n-files-upload>

        <n-files-list v-if="showList === true" :fileList="fileList" @openFile="openFile" @replaceFile="replaceFile" @deleteFile="deleteFile">
            <template v-slot:list-empty>
                <slot name="list-empty" />
            </template>
            <template v-slot:list-items>
                <slot name="list-items" />
            </template>
            <template v-if="passSlot('list-item-open')" v-slot:list-item-open>
                <slot name="list-item-open" />
            </template>
            <template v-if="passSlot('list-item-download')" v-slot:list-item-download>
                <slot name="list-item-download" />
            </template>
            <template v-if="passSlot('list-item-replace')" v-slot:list-item-replace>
                <slot name="list-item-replace" />
            </template>
            <template v-if="passSlot('list-item-delete')" v-slot:list-item-delete>
                <slot name="list-item-delete" />
            </template>
        </n-files-list>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { wait } from '@/mixins/shared/helpers'
import { upload } from '@/mixins/shared/files-upload.service'
// import { upload } from '@/file-upload.fake.service' // Fake service
// import { upload } from '@/file-upload.service'; // Real service
import NFilesUpload from './files-upload.vue'
import NFilesList from './files-list.vue'

export default {
    name: 'NFiles',
    components: {
        NFilesUpload,
        NFilesList
    },
    props: {
        showUpload: {
            type: Boolean,
            default: true
        },
        allowedFileTypes: {
            type: String,
            default: '*'
        },
        showList: {
            type: Boolean,
            default: true
        },
        populateFileList: {
            type: Array,
            default: () => []
        },
        uploadFunction: {
            type: Function,
            default: null
        },
        uploadStatus: {
            type: String,
            default: 'initial'
        },
        uploadErrorResponse: {
            type: [String, Object, Error],
            default: null
        },
        openFunction: {
            type: Function,
            default: null
        },
        replaceFunction: {
            type: Function,
            default: null
        },
        deleteFunction: {
            type: Function,
            default: null
        }
    },
    data: function () {
        return {
            fileList: [],
            replaceFileIndex: null,
            uploadStatusTimer: null
        }
    },
    computed: {
        ...mapGetters({
            getEnquiryUUID: 'getEnquiryUUID'
        })
    },
    watch: {
        populateFileList: {
            immediate: true,
            handler (value) {
                this.fileList = value
            }
        }
    },
    methods: {
        showSlot (slotName) {
            if (this.$slots.hasOwnProperty(slotName) && this.$slots[slotName] === undefined) {
                return false
            }

            return true
        },
        passSlot (slotName) {
            if (!this.$slots.hasOwnProperty(slotName)) {
                return false
            }

            return true
        },
        uploadFiles (element) {
            this.$emit('uploadFiles', element)

            if (this.uploadFunction) {
                this.uploadFunction(element)
                return false
            }

            const formData = new FormData()
            const startTime = performance.now()
            let endTime = startTime

            clearTimeout(this.uploadStatusTimer)
            this.uploadStatus = 'saving'
            Object.values(element.files).forEach((file) => {
                formData.append(element.name, file, file.name)
            })

            upload(formData)
                .then((result) => {
                    endTime = performance.now()

                    return result
                })
                .then(wait(250 - (endTime - startTime > 250 ? 250 : endTime - startTime))) // Make sure spinner is shown at least 250ms
                .then((result) => {
                    // Update filelist with added or replaced files
                    if (this.replaceFileIndex !== null) {
                        const arrayBefore = this.fileList.slice(0, this.replaceFileIndex)
                        const arrayAfter = this.fileList.slice(this.replaceFileIndex + 1)

                        this.fileList = [...arrayBefore, ...result, ...arrayAfter] // DEV ONLY
                    } else {
                        this.fileList = this.fileList.concat(result)
                    }
                    this.replaceFileIndex = null

                    this.uploadStatus = 'success'
                    document.getElementById(this.$children.find(child => child.inputId).inputId).value = ''
                    this.uploadStatusTimer = setTimeout(() => {
                        this.uploadStatus = 'initial'
                    }, 3500)
                })
                .catch((error) => {
                    console.error('Upload files', typeof error, error)
                    this.uploadErrorResponse = error
                    this.uploadStatus = 'failed'
                    this.uploadStatusTimer = setTimeout(() => {
                        this.uploadStatus = 'initial'
                    }, 5000)
                })
        },
        openFile (file) {
            this.$emit('openFile', file)

            if (this.openFunction) {
                this.openFunction(file)
                return false
            }

            // Open file in new window
            window.open(file.url, '_blank')
        },
        replaceFile (file) {
            this.$emit('replaceFile', file)

            if (this.replaceFunction) {
                this.replaceFunction(file)
                return false
            }

            this.replaceFileIndex = this.fileList.indexOf(file)
            // Click input field to start replacement
            document.getElementById(this.$children.find(child => child.inputId).inputId).click()
        },
        deleteFile (file) {
            this.$emit('deleteFile', file)

            if (this.deleteFunction) {
                this.deleteFunction(file)
                return false
            }

            this.fileList.splice(this.fileList.indexOf(file), 1)
        }
    },
    created: function () {
    },
    mounted: function () {
    }
}
</script>

<style lang="scss">
:root {
    --primaryColor: #243638;
	--buttonBackgroundColor: #d0ecef;
    --buttonShadowColor: rgba(36,54,56,0.25);
	--outlineColor: rgba(62,124,248,0.5);

    --ease-in-out-quad: cubic-bezier(0.455, 0.03, 0.515, 0.955); /* https://cubic-bezier.com/#0.455,0.03,0.515,0.955 */
    --ease-out-cubic: cubic-bezier(0.215, 0.61, 0.355, 1); /* https://cubic-bezier.com/#0.215,0.61,0.355,1 */
    --ease-in-out-cubic: cubic-bezier(0.645, 0.045, 0.355, 1); /* https://cubic-bezier.com/#0.645,0.045,0.355,1 */
}

.n-files {
    border-bottom: 2px solid var(--primaryColor);
    border-top: 2px solid var(--primaryColor);
    padding-bottom: 2em;
    padding-top: 2em;
    margin: 2em auto;
}
.n-files__heading {
    margin-bottom: 0;
}
.n-files__description {
    margin-top: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/shared/files/files.vue